import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  ArrowDown,
  Download,
  MessageCircle,
  ShieldCheck,
  GraduationCap,
  BadgeCheck,
} from "lucide-react";
import { COLLEGE } from "../data/college";
import { NeuralNet, HexRing } from "./Decorations";
import { useT } from "../i18n/LanguageProvider";

const Hero = () => {
  const { t } = useT();
  const waUrl = `https://wa.me/91${COLLEGE.phone_primary}?text=${encodeURIComponent(
    COLLEGE.whatsapp_message
  )}`;

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-[#04060a] pt-20"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <img
          src={COLLEGE.hero_bg}
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04060a]/55 via-[#04060a]/85 to-[#04060a]" />
      </div>
      <div className="absolute inset-0 bg-grid mask-radial opacity-70" />
      <NeuralNet className="absolute -right-20 top-10 w-[600px] opacity-40 hidden md:block" />
      <HexRing className="absolute -left-32 bottom-0 w-[500px] opacity-25 ring-rotate-rev hidden lg:block" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center w-full py-16 md:py-24">
        <div className="lg:col-span-8 space-y-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="kicker"
          >
            <span>{t("hero.est")}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-heading text-[2.25rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold leading-[0.95] tracking-tight"
          >
            <span className="block text-white">MAR ELIAS</span>
            <span className="block">
              <span className="text-[#00E5FF] text-glow-cyan">COLLEGE</span>
              <span className="text-white">.</span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-malayalam text-lg sm:text-2xl md:text-3xl text-[#39FF14] text-glow-neon max-w-2xl"
          >
            {COLLEGE.tagline_ml}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="font-body text-base sm:text-lg text-[#94a3b8] max-w-2xl leading-relaxed"
          >
            {t("hero.description")}
          </motion.p>

          {/* CTA group */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap items-center gap-3 sm:gap-4 pt-2"
          >
            <a
              href={`tel:+91${COLLEGE.phone_primary}`}
              className="btn-call !text-sm sm:!text-base"
              data-testid="hero-call-btn"
            >
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              {t("nav.call")} {COLLEGE.phone_primary}
            </a>
            <a
              href={COLLEGE.brochure_pdf}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="btn-outline"
              data-testid="hero-download-btn"
            >
              <Download className="w-4 h-4" />
              {t("btn.download")}
            </a>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-md border border-[#39FF14]/50 bg-[#39FF14]/10 text-[#39FF14] font-heading text-xs sm:text-sm uppercase tracking-[0.18em] hover:bg-[#39FF14]/20 hover:shadow-[0_0_24px_rgba(57,255,20,0.35)] transition"
            >
              <MessageCircle className="w-4 h-4" />
              {t("btn.whatsapp")}
            </a>
            <a href="#programs" className="btn-outline" data-testid="hero-explore-btn">
              {t("btn.explore")}
              <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>

          {/* Credential strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.38 }}
            className="flex flex-wrap gap-3 pt-6"
          >
            {[
              { icon: GraduationCap, label: t("hero.affiliated") },
              { icon: ShieldCheck, label: t("hero.approved") },
              { icon: BadgeCheck, label: t("hero.naac") },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 bg-white/[0.03]"
              >
                <Icon className="w-4 h-4 text-[#00E5FF]" />
                <span className="text-xs md:text-[13px] font-mono text-white/80 tracking-wide">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right showcase card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="lg:col-span-4 hidden lg:block"
        >
          <div className="relative">
            <div className="absolute -inset-px bg-gradient-to-br from-[#00E5FF]/40 via-transparent to-[#39FF14]/30 rounded-2xl blur" />
            <div className="relative glass p-7 brackets float-y">
              <div className="kicker mb-5">{t("hero.systemOnline")}</div>
              <div className="font-mono text-[11px] text-[#94a3b8] space-y-2.5">
                <div className="flex justify-between">
                  <span className="text-white/60 uppercase">{t("hero.programs")}</span>
                  <span className="text-[#00E5FF]">22+</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 uppercase">
                    {t("hero.specializations")}
                  </span>
                  <span className="text-[#00E5FF]">19</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 uppercase">
                    {t("hero.aiIntegrated")}
                  </span>
                  <span className="text-[#39FF14]">7 {t("hero.streams")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 uppercase">
                    {t("hero.established")}
                  </span>
                  <span className="text-[#00E5FF]">2006</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 uppercase">{t("hero.naacGrade")}</span>
                  <span className="text-[#39FF14]">B++</span>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="text-xs font-mono text-white/40 mb-2 uppercase">
                  {t("hero.enrollmentWindow")}
                </div>
                <div className="font-heading text-2xl text-white">2026 / 2027</div>
                <div className="text-xs text-[#94a3b8] mt-1">
                  {t("hero.minorityTag")}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-[#04060a]" />
    </section>
  );
};

export default Hero;
